import { useState } from 'react';

import {
  AlertCircle,
  FlaskConical,
  Loader2,
  Radio,
  RefreshCw,
  Square,
  Volume2,
  VolumeX,
  XCircle,
} from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { useCall } from '@/hooks/useCalls';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useReplayLive } from '@/hooks/useReplayLive';
import { useCancelReplay, useCreateReplay, useReplay } from '@/hooks/useReplays';
import {
  ACTIVE_REPLAY_STATUSES,
  REPLAY_RESULT_VARIANT,
  REPLAY_STATUS_VARIANT,
} from '@/lib/api/replays.constants';
import { extractErrorMessage } from '@/lib/utils';

import { AgentResultsPanel } from '../../calls/components/AgentResultsPanel';
import { RecordingPlayer } from '../../calls/components/RecordingPlayer';
import { TranscriptSection } from '../../calls/components/TranscriptSection';

import { ExpectationsPanel } from './components/ExpectationsPanel';

const formatElapsed = (ms: number): string => {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

export const ReplayDetailPage = () => {
  usePageTitle('Replay Detail');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  const { data: replay, isLoading } = useReplay(id);
  const { data: sourceCall } = useCall(replay?.source_call_id ?? undefined);
  const { data: testCall } = useCall(replay?.test_call_id ?? undefined);

  const cancelMutation = useCancelReplay();
  const createReplay = useCreateReplay();

  const isActive = replay ? ACTIVE_REPLAY_STATUSES.has(replay.status) : false;
  const isRunningVoice = replay?.status === 'running' && replay?.modality === 'VOICE';
  const judgeOutput = replay?.judge_output;
  const scenarioOutput =
    judgeOutput && Array.isArray(judgeOutput.checks)
      ? {
          scenarioName: judgeOutput.scenarioName ?? '',
          checks: judgeOutput.checks,
          rules: judgeOutput.rules ?? [],
          endedBy: judgeOutput.endedBy,
          verdict: judgeOutput.verdict,
        }
      : null;

  const [muted, setMuted] = useState(false);

  const {
    listening,
    ended,
    endedReason,
    turns,
    latestAt,
    startListening,
    stopListening,
    setMuted: setPlayerMuted,
  } = useReplayLive({
    testCaseId: replay?.id,
    enabled: isRunningVoice,
  });

  const handleToggleListen = () => {
    if (listening) {
      stopListening();
    } else {
      startListening();
    }
  };

  const handleToggleMute = () => {
    const next = !muted;
    setMuted(next);
    setPlayerMuted(next);
  };

  const handleCancel = async () => {
    if (!replay) return;
    try {
      await cancelMutation.mutateAsync(replay.id);
      toast({ title: 'Cancelled', description: 'Replay has been cancelled.' });
    } catch {
      toast({ variant: 'destructive', title: 'Error', description: 'Failed to cancel replay.' });
    }
  };

  const handleReplayAgain = async () => {
    if (!replay?.source_call_id) return;
    try {
      const next = await createReplay.mutateAsync({
        source_call_id: replay.source_call_id,
        modality: replay.modality,
        commit_id: replay.commit_id ?? undefined,
      });
      toast({ title: 'Replay started', description: 'A new replay has been created.' });
      navigate(`/agent-improvement/replays/${next.id}`);
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Failed to start replay',
        description: extractErrorMessage(err),
      });
    }
  };

  if (isLoading || !replay) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <FlaskConical className="h-5 w-5" />
            <p className="text-muted-foreground text-sm tracking-[0.3em] uppercase">
              Replay detail
            </p>
          </div>
          <h1 className="text-2xl font-bold tracking-tight break-all">{replay.id}</h1>

          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={REPLAY_STATUS_VARIANT[replay.status]}>{replay.status}</Badge>
            {replay.result && (
              <Badge variant={REPLAY_RESULT_VARIANT[replay.result]}>{replay.result}</Badge>
            )}
            <Badge variant="outline">{replay.channel}</Badge>
            <Badge variant="outline">{replay.modality}</Badge>
            {replay.commit_id && (
              <Badge variant="secondary" className="font-mono text-xs">
                commit: {replay.commit_id.slice(0, 8)}
              </Badge>
            )}
            <Badge variant="secondary">{replay.turns} turns</Badge>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Live listen button — only for running VOICE replays */}
          {isRunningVoice && (
            <Button
              variant={listening ? 'default' : 'outline'}
              size="sm"
              onClick={handleToggleListen}
            >
              {listening ? (
                <>
                  <Square className="mr-1.5 h-4 w-4" />
                  Stop listening
                </>
              ) : (
                <>
                  <Radio className="mr-1.5 h-4 w-4" />
                  Listen live
                </>
              )}
            </Button>
          )}

          {isActive && (
            <Button
              variant="outline"
              size="sm"
              disabled={cancelMutation.isPending}
              onClick={() => void handleCancel()}
            >
              <XCircle className="mr-1.5 h-4 w-4" />
              Cancel
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            disabled={createReplay.isPending}
            onClick={() => void handleReplayAgain()}
          >
            <RefreshCw className="mr-1.5 h-4 w-4" />
            Replay again
          </Button>
          <Button variant="outline" size="sm" asChild>
            <Link to="/agent-improvement/replays">Back to replays</Link>
          </Button>
        </div>
      </div>

      {/* Live indicator bar */}
      {(listening || ended) && (
        <div className="flex items-center gap-3 rounded-md border px-4 py-2">
          {listening && (
            <>
              <span className="relative flex h-2.5 w-2.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-red-500" />
              </span>
              <span className="text-sm font-medium">Live</span>
              <span className="text-muted-foreground text-sm">{formatElapsed(latestAt)}</span>
              <Button
                variant="ghost"
                size="sm"
                className="ml-auto h-7 w-7 p-0"
                aria-label={muted ? 'Unmute' : 'Mute'}
                onClick={handleToggleMute}
              >
                {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
              </Button>
            </>
          )}
          {ended && (
            <span className="text-muted-foreground text-sm">
              Replay ended{endedReason ? `: ${endedReason}` : ''}
            </span>
          )}
        </div>
      )}

      {/* Error banner */}
      {replay.status === 'failed' && replay.error && (
        <Card className="border-destructive">
          <CardContent className="flex items-start gap-3 pt-4">
            <AlertCircle className="text-destructive mt-0.5 h-5 w-5 shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-medium">Replay failed</p>
              <p className="text-muted-foreground mt-1 text-sm break-all">{replay.error}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Scenario expectations (checks and rules) */}
      {scenarioOutput && <ExpectationsPanel output={scenarioOutput} />}

      {/* Verdict reasons */}
      {!scenarioOutput && judgeOutput?.verdict && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Verdict</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Badge variant={REPLAY_RESULT_VARIANT[judgeOutput.verdict.result]}>
                {judgeOutput.verdict.result}
              </Badge>
              {judgeOutput.endedBy && (
                <span className="text-muted-foreground text-xs">
                  ended by: {judgeOutput.endedBy}
                </span>
              )}
            </div>
            {judgeOutput.verdict.reasons.length > 0 && (
              <ul className="text-muted-foreground mt-2 list-inside list-disc space-y-1 text-sm">
                {judgeOutput.verdict.reasons.map((reason, i) => (
                  <li key={i}>{reason}</li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      )}

      {/* Two-column comparison */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Original call */}
        <div className="space-y-4">
          <h2 className="text-lg font-semibold">Original</h2>
          {sourceCall ? (
            <>
              <div className="text-xs">
                <Link to={`/calls/${sourceCall.id}`} className="text-primary hover:underline">
                  {sourceCall.id.slice(0, 8)}... &mdash; {sourceCall.status}
                </Link>
              </div>
              <TranscriptSection
                transcript={sourceCall.transcript}
                enhancedTranscript={sourceCall.enhanced_transcript}
                originalTranscript={sourceCall.original_transcript ?? sourceCall.transcript}
                hasEnhancedTranscriptFlag={sourceCall.has_enhanced_transcript}
              />
              <RecordingPlayer
                callId={sourceCall.id}
                recordingPath={sourceCall.recording_path}
                internalRecordingPath={sourceCall.internal_recording_path}
              />
              <AgentResultsPanel call={sourceCall} />
            </>
          ) : (
            <p className="text-muted-foreground text-sm">Loading source call...</p>
          )}
        </div>

        {/* Test call */}
        <div className="space-y-4">
          <h2 className="text-lg font-semibold">Test</h2>

          {/* Live transcript panel — shown when listening or after ended (while still on running) */}
          {(listening || ended) && turns.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Live transcript</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="max-h-64 space-y-2 overflow-y-auto">
                  {turns.map((turn, i) => (
                    <div
                      key={i}
                      className={`flex ${turn.role === 'agent' ? 'justify-start' : 'justify-end'}`}
                    >
                      <div
                        className={`max-w-[80%] rounded-lg px-3 py-1.5 text-sm ${
                          turn.role === 'agent'
                            ? 'bg-muted text-foreground'
                            : 'bg-primary text-primary-foreground'
                        }`}
                      >
                        <p>{turn.text}</p>
                        <p className="mt-0.5 text-[10px] opacity-60">{formatElapsed(turn.at)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {replay.test_call_id ? (
            testCall ? (
              <>
                <div className="text-xs">
                  <Link to={`/calls/${testCall.id}`} className="text-primary hover:underline">
                    {testCall.id.slice(0, 8)}... &mdash; {testCall.status}
                  </Link>
                </div>
                <TranscriptSection
                  transcript={testCall.transcript}
                  enhancedTranscript={testCall.enhanced_transcript}
                  originalTranscript={testCall.original_transcript ?? testCall.transcript}
                  hasEnhancedTranscriptFlag={testCall.has_enhanced_transcript}
                />
                <RecordingPlayer
                  callId={testCall.id}
                  recordingPath={testCall.recording_path}
                  internalRecordingPath={testCall.internal_recording_path}
                />
                <AgentResultsPanel call={testCall} />
              </>
            ) : (
              <p className="text-muted-foreground text-sm">Loading test call...</p>
            )
          ) : isActive ? (
            <div className="flex items-center gap-2 py-8">
              <Loader2 className="h-4 w-4 animate-spin" />
              <p className="text-muted-foreground text-sm">
                {replay.status === 'pending'
                  ? 'Waiting to start...'
                  : replay.status === 'running'
                    ? 'Replay in progress...'
                    : 'Judging result...'}
              </p>
            </div>
          ) : (
            <p className="text-muted-foreground text-sm">No test call produced.</p>
          )}

          {/* Inline judge quality from judge_output */}
          {judgeOutput?.quality && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Judge quality assessment</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {judgeOutput.quality.flags && Object.keys(judgeOutput.quality.flags).length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {Object.entries(judgeOutput.quality.flags).map(([key, value]) => (
                      <Badge key={key} variant={value ? 'default' : 'outline'} className="text-xs">
                        {key}: {String(value)}
                      </Badge>
                    ))}
                  </div>
                )}
                {judgeOutput.quality.reasoning && (
                  <p className="text-muted-foreground text-sm">{judgeOutput.quality.reasoning}</p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Simulated (intercepted) tool invocations */}
          {replay.intercepted_effects.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Tools (simulated)</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm">
                  {replay.intercepted_effects.map((effect, i) => {
                    const e = effect as Record<string, unknown>;
                    const toolName = String(e.tool ?? 'unknown');
                    const timestamp =
                      typeof e.at === 'string' ? new Date(e.at).toLocaleTimeString() : null;
                    const args =
                      e.args && typeof e.args === 'object'
                        ? (e.args as Record<string, unknown>)
                        : null;
                    return (
                      <li key={i} className="flex flex-col gap-0.5">
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary" className="text-xs">
                            {toolName}
                          </Badge>
                          {timestamp && (
                            <span className="text-muted-foreground text-xs">{timestamp}</span>
                          )}
                        </div>
                        {args && Object.keys(args).length > 0 && (
                          <pre className="text-muted-foreground overflow-x-auto text-xs">
                            {JSON.stringify(args, null, 2)}
                          </pre>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};
